'use client';

import { useRef, useState } from 'react';
import { ConfirmDialog } from '@/components/dashboard/ConfirmDialog';
import { useDeskToast } from './desk-feedback';
import {
  Building2,
  Database,
  Save,
  Download,
  Upload,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import { officeLocations, type AgencyProfile } from '@/lib/agency';
import { api, ApiError } from '@/lib/api';
import { CURRENCIES, normalizeCurrency } from '@/lib/currency';
import { describeDeskImport, parseDeskImport } from '@/lib/desk/import-bundle';
import { MOROCCAN_LOCATIONS } from '@/lib/morocco';

interface AgencyModuleProps {
  agency: AgencyProfile;
  setAgency: (a: AgencyProfile) => void;
  backupData: object;
  onImport: (data: unknown) => { ok: boolean; message: string };
  jsonImportAllowed?: boolean;
}

type FieldKey =
  | 'name'
  | 'shortName'
  | 'city'
  | 'address'
  | 'phone'
  | 'whatsapp'
  | 'email'
  | 'rc'
  | 'ice'
  | 'patente'
  | 'currency'
  | 'contractFooter';

const FIELDS: { key: FieldKey; label: string; placeholder?: string; full?: boolean }[] = [
  { key: 'name', label: 'Nom agence', placeholder: 'fleetava Agence Agadir' },
  { key: 'shortName', label: 'Nom court', placeholder: 'fleetava' },
  { key: 'city', label: 'Ville', placeholder: 'Agadir' },
  { key: 'address', label: 'Adresse', placeholder: 'Av. Hassan II, Agadir 80000' },
  { key: 'phone', label: 'Téléphone', placeholder: '+212528840000' },
  { key: 'whatsapp', label: 'WhatsApp', placeholder: '+212661000000' },
  { key: 'email', label: 'Email', placeholder: 'contact@fleetava-agadir.ma' },
  { key: 'rc', label: 'RC', placeholder: 'RC 123456' },
  { key: 'ice', label: 'ICE', placeholder: 'ICE 00234567800001' },
  { key: 'patente', label: 'Patente', placeholder: 'Patente 45678901' },
  { key: 'currency', label: 'Devise', placeholder: 'DH' },
  { key: 'contractFooter', label: 'Pied de contrat', full: true },
];

const inputCls =
  'w-full rounded-2xl border border-line bg-shell px-3 py-2 text-xs text-ink outline-none focus:border-brand focus:bg-card';
const labelCls = 'mb-1 block text-xs font-medium text-muted';

export function AgencyModule({ agency, setAgency, backupData, onImport, jsonImportAllowed = false }: AgencyModuleProps) {
  const notify = useDeskToast();
  const [form, setForm] = useState<AgencyProfile>(agency);
  const [importMsg, setImportMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [pendingImport, setPendingImport] = useState<{ data: unknown; summary: string } | null>(null);
  const [importing, setImporting] = useState(false);
  const [zones, setZones] = useState(() => officeLocations(agency, MOROCCAN_LOCATIONS).join('\n'));
  const [whatsappTemplate, setWhatsappTemplate] = useState(agency.whatsappTemplate ?? '');
  const [rateMsg, setRateMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const setField = (key: FieldKey, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSaveProfile = () => {
    setAgency(form);
    notify('Profil enregistré.');
  };

  const handleExport = () => {
    const date = new Date().toISOString().slice(0, 10);
    const blob = new Blob([JSON.stringify(backupData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fleetava_backup_${date}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleSaveRates = () => {
    setRateMsg(null);
    const rateEur = Number(form.rateEur);
    const rateUsd = Number(form.rateUsd);
    if (!(rateEur > 0) || !(rateUsd > 0)) {
      setRateMsg({ ok: false, text: 'Les deux taux doivent être supérieurs à 0.' });
      return;
    }
    // Keep unsaved edits from the identity fields: the bulk sync sends the
    // whole profile, so using the stale `agency` prop would overwrite them.
    const next = { ...agency, ...form, rateEur, rateUsd };
    setForm(next);
    setAgency(next);
    setRateMsg({ ok: true, text: `Taux enregistrés : 1 € = ${rateEur} DH · 1 $ = ${rateUsd} DH.` });
  };

  const handleImportFile = async (file: File | undefined) => {
    setImportMsg(null);
    setPendingImport(null);
    if (!file) return;
    if (!jsonImportAllowed) {
      setImportMsg({ ok: false, text: 'JSON import is disabled for this account or in production. Use the fleet CSV import for vehicles.' });
      return;
    }
    try {
      const text = await file.text();
      const data: unknown = JSON.parse(text);
      const parsed = parseDeskImport(data);
      if (!parsed.ok) {
        setImportMsg({ ok: false, text: parsed.message });
        return;
      }
      setPendingImport({ data, summary: describeDeskImport(parsed.bundle) });
    } catch {
      setImportMsg({ ok: false, text: 'Fichier invalide : JSON illisible.' });
    }
  };

  const confirmImport = async () => {
    if (!pendingImport) return;
    setImporting(true);
    setImportMsg(null);
    try {
      await api<{ backupId: string }>('/api/agency/import-snapshot', { method: 'POST', body: '{}' });
      const result = onImport(pendingImport.data);
      setImportMsg({ ok: result.ok, text: result.ok ? `Backup saved. ${result.message}` : result.message });
      if (result.ok) setPendingImport(null);
    } catch (err: unknown) {
      const message = err instanceof ApiError ? err.message : 'The backup did not complete, so nothing was replaced.';
      setImportMsg({ ok: false, text: message });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* (a) Identité agence */}
      <section className="rounded-3xl border border-line bg-card p-4">
        <div className="mb-3 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-2xl bg-ink text-shell">
            <Building2 size={16} />
          </span>
          <div>
            <h2 className="text-xs font-bold text-ink">Identité agence</h2>
            <p className="text-xs text-muted">
              Utilisée sur les contrats, reçus et en-têtes.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {FIELDS.map((f) => (
            <div key={f.key} className={f.full ? 'sm:col-span-2' : ''}>
              <label className={labelCls}>{f.label}</label>
              {f.key === 'contractFooter' ? (
                <textarea
                  rows={2}
                  className={`${inputCls} resize-none`}
                  value={form[f.key]}
                  onChange={(e) => setField(f.key, e.target.value)}
                />
              ) : f.key === 'currency' ? (
                <select
                  className={inputCls}
                  value={normalizeCurrency(form.currency)}
                  onChange={(e) => setField(f.key, e.target.value)}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  className={inputCls}
                  placeholder={f.placeholder}
                  value={form[f.key]}
                  onChange={(e) => setField(f.key, e.target.value)}
                />
              )}
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={handleSaveProfile}
          className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-ink px-4 py-2 text-xs font-semibold text-shell hover:brightness-110"
        >
          <Save size={14} />
          Enregistrer le profil
        </button>
      </section>

      {/* (a2) Taux de change touristes */}
      <section className="rounded-3xl border border-line bg-card p-4">
        <div className="mb-3 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-2xl bg-success text-white">
            <RefreshCw size={16} />
          </span>
          <div>
            <h2 className="text-xs font-bold text-ink">Taux de change touristes</h2>
            <p className="text-xs text-muted">
              Le site affiche les prix en {normalizeCurrency(form.currency) === 'MAD' ? 'DH' : form.currency} avec
              l’équivalent € / $ calculé sur ces taux (1 € = X DH).
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className={labelCls}>1 € = … DH</label>
            <input
              type="number" min={0.001} step={0.001}
              className={inputCls}
              value={form.rateEur ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, rateEur: Number(e.target.value) }))}
            />
          </div>
          <div>
            <label className={labelCls}>1 $ = … DH</label>
            <input
              type="number" min={0.001} step={0.001}
              className={inputCls}
              value={form.rateUsd ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, rateUsd: Number(e.target.value) }))}
            />
          </div>
        </div>
        {rateMsg && (
          <p className={`mt-2 text-xs font-semibold ${rateMsg.ok ? 'text-success' : 'text-danger'}`}>
            {rateMsg.text}
          </p>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button" onClick={handleSaveRates}
            className="inline-flex items-center gap-2 rounded-2xl bg-ink px-4 py-2 text-xs font-semibold text-shell hover:brightness-110"
          >
            <Save size={14} />
            Enregistrer les taux
          </button>
        </div>
      </section>

      <section className="rounded-3xl border border-line bg-card p-4">
        <h2 className="text-xs font-bold text-ink">Zones de remise et WhatsApp</h2>
        <p className="mt-1 text-xs text-muted">
          One zone per line. The confirmation can use {'{name}'}, {'{id}'}, {'{vehicle}'}, {'{pickup}'}, {'{date}'}, {'{time}'}, {'{status}'}, and {'{agency}'}.
        </p>
        <label className={`${labelCls} mt-3`}>Zones</label>
        <textarea rows={6} className={`${inputCls} resize-y`} value={zones} onChange={(event) => setZones(event.target.value)} />
        <label className={`${labelCls} mt-3`}>Message WhatsApp</label>
        <textarea
          rows={5}
          className={`${inputCls} resize-y`}
          value={whatsappTemplate}
          onChange={(event) => setWhatsappTemplate(event.target.value)}
          placeholder={'Bonjour {name},\nVotre réservation {id} est {status}.\nVéhicule: {vehicle}\nRetrait: {pickup} le {date} à {time}\nMerci — {agency}'}
        />
        <button
          type="button"
          onClick={() => {
            const pickupLocations = zones.split('\n').map((line) => line.trim()).filter(Boolean);
            const next = { ...agency, ...form, pickupLocations, whatsappTemplate };
            setAgency(next);
            setForm(next);
          }}
          className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-ink px-4 py-2 text-xs font-semibold text-shell"
        >
          <Save size={14} />
          Enregistrer zones et message
        </button>
      </section>

      {/* (c) Sauvegarde / Restauration */}
      <section className="rounded-3xl border border-line bg-card p-4">
        <div className="mb-3 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-2xl bg-ink text-shell">
            <Database size={16} />
          </span>
          <div>
            <h2 className="text-xs font-bold text-ink">Sauvegarde / Restauration</h2>
            <p className="text-xs text-muted">
              {jsonImportAllowed
                ? 'Import replaces office lists after a backup and a confirmation. Vehicles and bookings in the file are ignored.'
                : 'JSON import is disabled for this account or in production. Export stays available. Load a fleet with the CSV command.'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleExport}
            className="inline-flex items-center gap-2 rounded-2xl bg-ink px-4 py-2 text-xs font-semibold text-shell hover:brightness-110"
          >
            <Download size={14} />
            Exporter JSON
          </button>
          {jsonImportAllowed && (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-2 rounded-2xl border border-line bg-card px-4 py-2 text-xs font-semibold text-ink hover:bg-shell"
            >
              <Upload size={14} />
              Importer JSON
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => {
              void handleImportFile(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </div>
        <ConfirmDialog
          open={pendingImport !== null}
          title="Replace the current office data?"
          description={pendingImport ? `${pendingImport.summary}. A backup file is written before anything changes.` : 'A backup file is written before anything changes.'}
          confirmLabel="Replace office data"
          pendingLabel="Backing up…"
          pending={importing}
          error={importMsg && !importMsg.ok ? importMsg.text : null}
          onCancel={() => {
            if (importing) return;
            setPendingImport(null);
            setImportMsg(null);
          }}
          onConfirm={() => confirmImport()}
        />
        {importMsg && (
          <p
            className={`mt-2 flex items-center gap-1 text-xs font-medium ${
              importMsg.ok ? 'text-success' : 'text-danger'
            }`}
          >
            {importMsg.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
            {importMsg.text}
          </p>
        )}
        <p className="mt-2 flex items-start gap-1 text-xs text-warning">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          Les photos enregistrées dans les réservations peuvent rendre le fichier lourd.
        </p>
      </section>
    </div>
  );
}
